import React, { useCallback, useEffect, useRef } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';
import { PaneHandle } from '../layout/PaneHandle';
import { PANE_STYLE } from './PaneShell';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * HomeConsole — the geometry of the project console, and nothing else.
 *
 * Panes arrive as ready-made nodes, so this file owns layout and Home owns data.
 *
 * ── Why a split tree and not a widget grid ───────────────────────────────────
 * Once the page stops scrolling, the panes tile the viewport — and a tiling of
 * a rectangle is a split tree, not a free canvas. Going the other way (drag
 * anything anywhere, á la react-grid-layout) costs a dependency, pixel row
 * arithmetic that fights internal scrolling, a second mobile code path, and it
 * makes the app read as a customisable SaaS dashboard, which is the one thing
 * the design guidelines say not to build. Five resizable panes cost five
 * numbers and no new dependency.
 *
 * ── The seams ────────────────────────────────────────────────────────────────
 * Handles are 12px, which is the shell's `md:gap-3`. A boundary inside the
 * console is therefore visually identical to a boundary between top-level
 * surfaces — the ambient canvas showing through a seam is the drag target. The
 * groups carry no `gap` of their own; that would double the seam.
 *
 * ── Persistence ──────────────────────────────────────────────────────────────
 * `autoSaveId` stores each group's layout under
 * `localStorage["react-resizable-panels:" + id]`, as a map keyed by the panel
 * set. Panels therefore carry explicit ids: with them, the read-only tree hashes
 * to its own entry and can never inherit a read-write layout. Without them the
 * key falls back to a hash of the size constraints, and a stored layout whose
 * length no longer matches the panel set makes the library *throw* from inside
 * a layout effect — a blank route, not a graceful reset.
 *
 * Not keyed by project: unlike the editor's split, which depends on that
 * project's file tree, these proportions are a preference about a pane set that
 * is identical everywhere. Tune it once.
 *
 * `order` is 1-based on purpose — the library tests `panel.order ? … : …`, so
 * `order={0}` is falsy and silently drops out of the key.
 *
 * ── What the scratchpad does to all of this ──────────────────────────────────
 * Three things, in increasing order of how much room it asks for:
 *
 *  1. Open, it takes the signals column and the bar steps into the left one.
 *  2. Divided into two or three text columns, it grows the seam to fit them —
 *     and gives the width back if you never touched the seam yourself.
 *  3. Split into two drafts, it takes the console outright: the dashboard
 *     collapses to zero and the bar returns to spanning the route.
 */

const LAYOUT_VERSION = 1;

// Sum to 100 per group, per mode. A short branch doesn't just look wrong: the
// library normalises it and warns, so the first drag would jump.
const DEFAULTS = {
    columns: [62, 38],
    command: [58, 42],
    signalsFull: [38, 30, 32],
    signalsLean: [55, 45],
};

/**
 * The floor a draft of N text columns deserves, as a share of the console.
 *
 * One column asks for nothing: whatever the seam was left at is right, and the
 * two/three numbers only ever apply as a *minimum*, so a wide seam is never
 * narrowed to meet them. 58 is comfortably inside what the `command` panel's
 * `minSize={34}` allows, so nothing here needs a `maxSize` to stay legal.
 */
const DRAFT_SHARE = { 1: 0, 2: 46, 3: 58 };

// `overflow: visible` beats the library's inline `overflow: hidden`, which would
// otherwise shear every tile's drop shadow off at the pane edge — the exact
// thing the project Layout refuses to do to its surfaces. Only `style` can win
// against an inline value; a Tailwind class cannot.
const GROUP_SPILL = { overflow: 'visible' };

export function HomeConsole({ panes, bar, readOnly, draftSplit, draftColumns = 1 }) {
    const isMobile = useIsMobile();

    const columns = useRef(null);
    const command = useRef(null);
    const commandPanel = useRef(null);
    const signalsPanel = useRef(null);
    const signals = useRef(null);

    const hasAdvisor = !!panes.advisor;
    const hasChats = !!panes.chats;
    // Booleans, not the nodes: `panes.draft` is a fresh JSX element on every
    // render of Home, so an effect depending on it would run on every keystroke
    // in the advisor composer.
    const hasDraft = !!panes.draft;
    const split = hasDraft && !!draftSplit;

    const mode = readOnly ? 'ro' : 'rw';
    const saveId = (part) => `forge-ai:home:v${LAYOUT_VERSION}:${mode}:${part}`;

    const resetColumns = useCallback(() => columns.current?.setLayout(DEFAULTS.columns), []);
    const resetCommand = useCallback(() => command.current?.setLayout(DEFAULTS.command), []);
    const resetSignals = useCallback(
        () => signals.current?.setLayout(hasChats ? DEFAULTS.signalsFull : DEFAULTS.signalsLean),
        [hasChats]
    );

    /**
     * Two drafts take the console.
     *
     * The dashboard is *collapsed*, not unmounted. Conditionally rendering the
     * left column would re-mount everything in it on every toggle — the same
     * reason its wrapper div below is unconditional — and the advisor composer
     * would lose whatever you had typed into it each time.
     *
     * The pre-split layout is remembered here rather than left to the library's
     * expand(), which restores a size it inferred rather than the one you chose.
     */
    const preSplit = useRef(null);
    useEffect(() => {
        const group = columns.current;
        const panel = commandPanel.current;
        if (!group || !panel) return;

        if (split) {
            if (!panel.isCollapsed()) preSplit.current = group.getLayout();
            panel.collapse();
            return;
        }
        if (preSplit.current) {
            group.setLayout(preSplit.current);
            preSplit.current = null;
        } else if (panel.isCollapsed()) {
            // A reload that landed on a layout `autoSaveId` persisted mid-split.
            // The handle is disabled while split, so there would be nothing left
            // on screen to drag the dashboard back with.
            group.setLayout(DEFAULTS.columns);
        }
    }, [split]);

    /**
     * A note divided into columns is a wider note.
     *
     * The floating dock has always done this — `DOCK_WIDTH` steps 440 → 660 →
     * 880 with the count — but the inline pane just kept whatever share of the
     * console was persisted, so a three-column draft in the signals column got
     * about 130px per column.
     *
     * Growing is unconditional; shrinking is not. `auto` remembers what the seam
     * was before we moved it, and gives it back only if it is still exactly
     * where we left it. Drag it yourself and the memory stops matching, which is
     * the whole test: from then on your width outranks ours and merging a column
     * away moves nothing.
     */
    const auto = useRef(null);
    useEffect(() => {
        const panel = signalsPanel.current;
        if (!panel || !hasDraft || split) {
            auto.current = null;
            return;
        }

        const target = DRAFT_SHARE[draftColumns] ?? 0;
        const current = panel.getSize();

        if (target > current + 0.5) {
            // `from` is recorded once, at the first break. Re-recording it on
            // the second would make 3 → 1 stop at the two-column width.
            auto.current = { from: auto.current?.from ?? current, to: target };
            panel.resize(target);
            return;
        }

        if (auto.current && Math.abs(current - auto.current.to) < 0.5) {
            const back = Math.max(target, auto.current.from);
            panel.resize(back);
            auto.current = back > auto.current.from + 0.5 ? { ...auto.current, to: back } : null;
        }
    }, [draftColumns, hasDraft, split]);

    // Below md there is nothing to tile and a 12px handle is not a touch target,
    // so the panes become an ordinary scrolling stack. Fixed heights rather than
    // auto: the panes clip and scroll internally by design, and a content-sized
    // pane would defeat that on the one viewport where it matters most.
    if (isMobile) {
        return (
            <>
                {bar}
                <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 pb-28 pt-3">
                    <div className="h-[380px] flex-shrink-0">{panes.now}</div>
                    {hasAdvisor ? <div className="h-[210px] flex-shrink-0">{panes.advisor}</div> : null}
                    <div className="h-[280px] flex-shrink-0">{panes.artifacts}</div>
                    {hasChats ? <div className="h-[260px] flex-shrink-0">{panes.chats}</div> : null}
                    <div className="h-[300px] flex-shrink-0">{panes.pulse}</div>
                </div>
            </>
        );
    }

    // A single-pane group would be a group with nothing to resize, so read-only
    // drops the wrapper entirely rather than nesting a one-panel PanelGroup.
    //
    // Container Panels are left as block boxes on purpose. PanelGroup already
    // carries inline `height/width: 100%`, which fills a block parent exactly;
    // making the Panel a flex container instead would turn a lone <Surface>
    // (the read-only branch) into a shrink-to-fit flex item half the pane wide.
    const commandColumn = hasAdvisor ? (
        <PanelGroup
            direction="vertical"
            autoSaveId={saveId('command')}
            ref={command}
            style={GROUP_SPILL}
            className="min-h-0 min-w-0"
        >
            <Panel id="now" order={1} defaultSize={DEFAULTS.command[0]} minSize={24} style={PANE_STYLE}>
                {panes.now}
            </Panel>
            <PaneHandle onReset={resetCommand} />
            <Panel id="advisor" order={2} defaultSize={DEFAULTS.command[1]} minSize={18} style={PANE_STYLE}>
                {panes.advisor}
            </Panel>
        </PanelGroup>
    ) : (
        panes.now
    );

    const signalsDefaults = hasChats ? DEFAULTS.signalsFull : DEFAULTS.signalsLean;

    // Writing a note on Home is the thing you are doing there, so it takes the
    // whole signals column rather than floating over it. Same panel, same seam,
    // same persisted proportion — Pinned/Chats/Pulse come straight back when the
    // scratchpad closes, at the width they were left.
    const signalsColumn = (
        <PanelGroup
            direction="vertical"
            autoSaveId={saveId('signals')}
            ref={signals}
            style={GROUP_SPILL}
            className="min-h-0 min-w-0"
        >
            <Panel id="artifacts" order={1} defaultSize={signalsDefaults[0]} minSize={12} style={PANE_STYLE}>
                {panes.artifacts}
            </Panel>

            {hasChats ? <PaneHandle onReset={resetSignals} /> : null}
            {hasChats ? (
                <Panel id="chats" order={2} defaultSize={signalsDefaults[1]} minSize={12} style={PANE_STYLE}>
                    {panes.chats}
                </Panel>
            ) : null}

            <PaneHandle onReset={resetSignals} />
            <Panel
                id="pulse"
                order={3}
                defaultSize={hasChats ? signalsDefaults[2] : signalsDefaults[1]}
                minSize={12}
                style={PANE_STYLE}
            >
                {panes.pulse}
            </Panel>
        </PanelGroup>
    );

    // The bar spans the console when there is nothing beside it, and moves
    // inside the left column when the scratchpad takes the right. That hands
    // the editor the bar's whole row — the tallest thing this layout has to
    // give — and it costs nothing, because the bar is the dashboard's header
    // and the dashboard is now that column.
    //
    // Split sends it back to the top, because there is no left column left to
    // sit in: two drafts hold the whole width, and the bar is the only thing
    // above them naming the project they belong to.
    //
    // Two slots for one node rather than one slot that resizes: the panes below
    // must not move either way, and they don't, because neither of these
    // positions is theirs. Only the bar re-mounts, and the bar holds nothing
    // worth keeping.
    const barSpans = !hasDraft || split;

    return (
        <>
            {barSpans ? bar : null}

            <PanelGroup
                direction="horizontal"
                autoSaveId={saveId('columns')}
                ref={columns}
                // Remount rather than mutate a live panel registry when the pane
                // set changes underneath it.
                key={mode}
                style={GROUP_SPILL}
                // The library hardcodes inline `height: 100%`, which as a flex
                // sibling of the bar would overflow by the bar's height. `flex-1`
                // supplies a `flex-basis: 0%` that takes precedence for the main
                // size.
                className="min-h-0 min-w-0 flex-1"
            >
                <Panel
                    id="command"
                    order={1}
                    ref={commandPanel}
                    defaultSize={DEFAULTS.columns[0]}
                    minSize={34}
                    // Only while there is a draft to hand the room to. Without
                    // one this stays exactly as rigid as it always was, so
                    // nobody discovers by accident that the seam now has a
                    // shut position.
                    collapsible={hasDraft}
                    collapsedSize={0}
                    style={PANE_STYLE}
                >
                    {/* The wrapper is unconditional so the column below never
                        changes depth. Wrapping only when a draft is open would
                        re-mount the whole left side every time you press `q` —
                        losing the composer's text and every pane's scroll. */}
                    <div className="flex h-full min-h-0 flex-col md:gap-3">
                        {barSpans ? null : bar}
                        <div className="min-h-0 flex-1">{commandColumn}</div>
                    </div>
                </Panel>

                {/* Disabled rather than removed while split: taking a handle out
                    changes the panel set, and the library keys its stored layout
                    on that set. */}
                <PaneHandle onReset={resetColumns} disabled={split} />

                <Panel
                    id="signals"
                    order={2}
                    ref={signalsPanel}
                    defaultSize={DEFAULTS.columns[1]}
                    // An editor needs more of a floor than a list of file names does.
                    minSize={hasDraft ? 28 : 20}
                    style={PANE_STYLE}
                >
                    {panes.draft ?? signalsColumn}
                </Panel>
            </PanelGroup>
        </>
    );
}

export default HomeConsole;
